// Register Component
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ContentCut, Spa, Visibility, VisibilityOff } from '@mui/icons-material';
import authService from '../../services/auth';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    tenant_name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    mobile: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Debounced mobile check - temporarily disabled due to database issues
  React.useEffect(() => {
    // Temporarily disable mobile checking to avoid database lock issues
    // TODO: Re-enable once database lock issues are resolved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.mobile]); // Only depend on mobile, not mobileError to prevent loop

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Starting registration process...');
    
    // Validate all required fields
    if (!formData.tenant_name || !formData.first_name || !formData.last_name || 
        !formData.email || !formData.password || !formData.mobile) {
      setError('Please fill in all required fields');
      return;
    }

    // Match backend validation rules (min 2 chars for names/salon)
    if (String(formData.tenant_name).trim().length < 2) {
      setError('Salon name must be at least 2 characters');
      return;
    }
    if (String(formData.first_name).trim().length < 2) {
      setError('First name must be at least 2 characters');
      return;
    }
    if (String(formData.last_name).trim().length < 2) {
      setError('Last name must be at least 2 characters');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Validate mobile number - must be exactly 10 digits
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(formData.mobile)) {
      setError('Mobile number must be exactly 10 digits');
      return;
    }

    // Prevent double submission
    if (loading) {
      console.log('Registration already in progress, preventing double submission');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Sending registration data:', formData);
      const response = await authService.register(formData);
      console.log('Registration response:', response);
      
      // Show success message and navigate to email confirmation page
      setError(''); // Clear any existing errors
      navigate('/email-confirmation', { 
        state: { 
          userData: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            tenant_name: formData.tenant_name
          }
        } 
      });
    } catch (err) {
      console.error('Registration error:', err);
      // Prefer backend validation errors when present.
      const apiErrors = err?.responseData?.data?.errors;
      if (apiErrors && typeof apiErrors === 'object') {
        const firstKey = Object.keys(apiErrors)[0];
        const firstMsg = firstKey ? apiErrors[firstKey] : null;
        setError(firstMsg || err.message || 'Registration failed');
      } else {
        setError(err.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #dc2cb0 0%, #E91E63 50%, #8E24AA 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
      }}
    >
      <Container maxWidth="md">
        <Card sx={{ boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4 }}>
            {/* Logo and Brand */}
            <Box textAlign="center" mb={4}>
              <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                <ContentCut sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                <Spa sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Click<span style={{ color: '#dc2cb0' }}>Looks</span>
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create Your Salon Account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Single Form Layout */}
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }} autoComplete="off">
              {/* Salon Name (full width) */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Salon Name"
                  name="tenant_name"
                  value={formData.tenant_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Bella Beauty Salon"
                  autoComplete="off"
                />
              </Box>

              {/* First Name + Last Name */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                />
              </Box>
              
              {/* Mobile Number (full width) */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  placeholder="1234567890"
                  helperText="Enter your 10-digit mobile number"
                  error={false}
                  autoComplete="off"
                  inputProps={{
                    maxLength: 10,
                    pattern: "[0-9]{10}"
                  }}
                  onInput={(e) => {
                    // Only allow digits
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  }}
                />
              </Box>

              {/* Email Address (full width) */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                />
              </Box>
              
              {/* Password with show/hide toggle */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  helperText="Minimum 6 characters"
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Submit Button */}
              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Creating Account...
                    </>
                  ) : (
                    'Create Salon Account'
                  )}
                </Button>
              </Box>
            </Box>

            {/* Login Link */}
            <Box textAlign="center" mt={4}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ textDecoration: 'none' }}
                >
                  Sign in here
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Register;
